import type { CongressCountdown } from "../../components/ui/CongressCard";
import {
  congressDemotesAtMs,
  congressEndsAtMs,
  deriveCongressPhase,
} from "../../../../api/src/shared/domain/congress-lifecycle";
import { deriveCongressCountdown } from "./deriveCongressCountdown";

const DAY_MS = 86_400_000;

/**
 * What a desktop congress surface says about where the congress stands: the
 * room banner, the For you block, the space page's phase band and the
 * Resources card all print the same status, so they read it from one place.
 *
 * Built on the shared lifecycle (`deriveCongressPhase`, `congressEndsAtMs`,
 * `congressDemotesAtMs`) and `deriveCongressCountdown` (the digits), never on
 * the dates again: those own the inclusive end day and the grace window.
 *
 * - `countdown`: before the congress, once the admin's countdown window is open.
 * - `upcoming`: before the congress, window not open yet.
 * - `live`: during the congress, with which day it is. Counted in whole days
 *   from the start instant, against the same inclusive end the phase uses, so
 *   the day never exceeds the count, whatever zone the admin typed the dates in.
 * - `ended`: after the congress; `catchUpUntil` is the last day of the
 *   post-congress window while it is open, null once it has closed or when the
 *   space keeps its congress look forever.
 * - `evergreen`: a space with no dates.
 *
 * Each variant carries the date it is about, so no reader re-checks a date the
 * phase already proved.
 */
export type CongressStatus =
  | {
      kind: "countdown";
      opensOn: Date;
      countdown: Extract<CongressCountdown, { phase: "pre" }>;
    }
  | { kind: "upcoming"; opensOn: Date }
  | { kind: "live"; day: number; dayCount: number }
  | { kind: "ended"; endedOn: Date; catchUpUntil: Date | null }
  | { kind: "evergreen" };

export function deriveCongressStatus(
  space: {
    startDate: string | null;
    endDate: string | null;
    countdownStartsAt: string | null;
    featuredDaysAfterEnd: number | null;
    autoDemoteAfterWindow: boolean;
  },
  now: Date
): CongressStatus {
  const phase = deriveCongressPhase(space, now);
  switch (phase) {
    case "evergreen":
      return { kind: "evergreen" };
    case "pre": {
      if (space.startDate === null)
        throw new Error(
          "should not happen: a `pre` phase is only derived when startDate is set"
        );
      const opensOn = new Date(space.startDate);
      const countdown = deriveCongressCountdown(space, now);
      return countdown.phase === "pre"
        ? { kind: "countdown", opensOn, countdown }
        : { kind: "upcoming", opensOn };
    }
    case "live": {
      if (space.startDate === null || space.endDate === null)
        throw new Error(
          "should not happen: a `live` phase is only derived when both dates are set"
        );
      const startMs = new Date(space.startDate).getTime();
      const endsMs = congressEndsAtMs(space.endDate);
      const dayCount = Math.round((endsMs - startMs) / DAY_MS);
      return {
        kind: "live",
        // A clock change inside the congress makes its last day an hour
        // longer than 24h: that hour still belongs to the last day.
        dayCount,
        day: Math.min(
          Math.floor((now.getTime() - startMs) / DAY_MS) + 1,
          dayCount
        ),
      };
    }
    case "post": {
      if (space.endDate === null)
        throw new Error(
          "should not happen: a `post` phase is only derived when endDate is set"
        );
      const demotesAtMs = congressDemotesAtMs(space);
      return {
        kind: "ended",
        endedOn: new Date(space.endDate),
        // The last instant of the window, one before the demotion instant. Its
        // UTC day is the last featured day, whatever zone the admin typed in.
        // Null when the space never demotes, or once it has.
        catchUpUntil:
          demotesAtMs === null || now.getTime() >= demotesAtMs
            ? null
            : new Date(demotesAtMs - 1),
      };
    }
    default:
      throw new Error(phase satisfies never);
  }
}
